import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  MoreVert as MoreVertIcon,
  YouTube as YouTubeIcon,
  Link as LinkIcon,
  Reorder as ReorderIcon
} from '@mui/icons-material';
import { API_BASE_URL } from '../../config';

const UGCVideos = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    embed_url: '',
    youtube_url: '',
    description: '',
    display_order: 0,
    is_active: true
  });

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/admin/ugc-videos`);
      const data = await response.json();
      
      if (data.success) {
        setVideos(data.videos);
      } else {
        setError(data.error || 'Failed to fetch UGC videos');
      }
    } catch (err) {
      setError('Failed to fetch UGC videos: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddVideo = () => {
    setEditingVideo(null);
    setFormData({
      title: '',
      embed_url: '',
      instagram_url: '',
      description: '',
      display_order: videos.length + 1,
      is_active: true
    });
    setDialogOpen(true);
  };

  const handleEditVideo = (video) => {
    setEditingVideo(video);
    setFormData({
      title: video.title,
      embed_url: video.embed_url,
      youtube_url: video.youtube_url || '',
      description: video.description || '',
      display_order: video.display_order,
      is_active: video.is_active
    });
    setDialogOpen(true);
    setAnchorEl(null);
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this UGC video?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/admin/ugc-videos/${videoId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      
      if (data.success) {
        setSuccess('UGC video deleted successfully');
        fetchVideos();
      } else {
        setError(data.error || 'Failed to delete UGC video');
      }
    } catch (err) {
      setError('Failed to delete UGC video: ' + err.message);
    }
    setAnchorEl(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingVideo 
        ? `${API_BASE_URL}/admin/ugc-videos/${editingVideo.id}`
        : `${API_BASE_URL}/admin/ugc-videos`;
      
      const method = editingVideo ? 'PUT' : 'POST';
      
      const payload = {
        ...formData,
        store_id: '1532365' // Default store ID
      };

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess(editingVideo ? 'UGC video updated successfully' : 'UGC video added successfully');
        setDialogOpen(false);
        fetchVideos();
      } else {
        setError(data.error || 'Failed to save UGC video');
      }
    } catch (err) {
      setError('Failed to save UGC video: ' + err.message);
    }
  };

  const handleMenuOpen = (event, video) => {
    setAnchorEl(event.currentTarget);
    setSelectedVideo(video);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedVideo(null);
  };

  const toggleVideoStatus = async (video) => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/ugc-videos/${video.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          is_active: !video.is_active
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess(`UGC video ${video.is_active ? 'deactivated' : 'activated'} successfully`);
        fetchVideos();
      } else {
        setError(data.error || 'Failed to update video status');
      }
    } catch (err) {
      setError('Failed to update video status: ' + err.message);
    }
    setAnchorEl(null);
  };

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          UGC Videos Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddVideo}
          sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}
        >
          Add UGC Video
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={clearMessages} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={clearMessages} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Order</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>YouTube URL</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {videos.map((video) => (
              <TableRow key={video.id}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ReorderIcon sx={{ color: 'text.secondary' }} />
                    {video.display_order}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {video.title}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 200 }}>
                    {video.description || 'No description'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {video.youtube_url ? (
                    <Tooltip title="View on YouTube">
                      <Button
                        size="small"
                        startIcon={<YouTubeIcon />}
                        href={video.youtube_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ textTransform: 'none' }}
                      >
                        View
                      </Button>
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No URL
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={video.is_active ? 'Active' : 'Inactive'}
                    color={video.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(video.created_at).toLocaleDateString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <IconButton
                    onClick={(e) => handleMenuOpen(e, video)}
                    size="small"
                  >
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleEditVideo(selectedVideo)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => toggleVideoStatus(selectedVideo)}>
          <ListItemIcon>
            {selectedVideo?.is_active ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText>
            {selectedVideo?.is_active ? 'Deactivate' : 'Activate'}
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleDeleteVideo(selectedVideo?.id)} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingVideo ? 'Edit UGC Video' : 'Add New UGC Video'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                label="Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                fullWidth
              />
              
              <TextField
                label="Embed URL"
                value={formData.embed_url}
                onChange={(e) => setFormData({ ...formData, embed_url: e.target.value })}
                required
                fullWidth
                helperText="YouTube embed URL (e.g., https://www.youtube.com/embed/ABC123)"
              />
              
              <TextField
                label="YouTube URL"
                value={formData.youtube_url}
                onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                fullWidth
                helperText="Direct YouTube video URL (optional)"
              />
              
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={3}
                fullWidth
              />
              
              <TextField
                label="Display Order"
                type="number"
                value={formData.display_order}
                onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                fullWidth
                helperText="Lower numbers appear first"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label="Active"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" sx={{ bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5' } }}>
              {editingVideo ? 'Update' : 'Add'} Video
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default UGCVideos;
